import React, { useContext, useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  useMediaQuery,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const NAV = [
  { label: "Home", href: "/" },
  { label: "Bill Payments", href: "#services" },
  { label: "Wallet", href: "#wallet" },
  { label: "About", href: "#about" },
];

export default function Header() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawer, setDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isLoggedIn = !!sessionStorage.getItem("SessionID");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled
            ? "rgba(255,255,255,0.97)"
            : "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          borderBottom: scrolled
            ? "1px solid #e8f5e9"
            : "1px solid transparent",
          transition: "all 0.3s ease",
          boxShadow: scrolled ? "0 2px 24px rgba(22,163,74,0.08)" : "none",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 2, md: 6 },
            minHeight: { xs: 60, md: 68 },
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 6px 18px rgba(22,163,74,.25)",
                overflow: "hidden",
                flexShrink: 0,
                bgcolor: "transparent",
              }}
            >
              <Box
                component="img"
                src="/Assets/ta_walletlogo.png"
                alt="TA Wallet"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "none", // IMPORTANT
                }}
                onError={(e) => {
                  console.error("Logo not found");
                  e.target.style.display = "none";
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1,
                  letterSpacing: "-0.3px",
                }}
              >
                TA Wallet
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#16a34a",
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: "0.4px",
                }}
              >
                ONE APP
              </Typography>
            </Box>
          </Box>

          {/* Desktop Nav */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {NAV.map((n) => (
                <Button
                  key={n.label}
                  onClick={() => {
                    if (n.href.startsWith("#")) {
                      document
                        .querySelector(n.href)
                        ?.scrollIntoView({ behavior: "smooth" });
                    } else navigate(n.href);
                  }}
                  sx={{
                    color: "#475569",
                    fontWeight: 500,
                    fontSize: 14,
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#f0fdf4", color: "#16a34a" },
                  }}
                >
                  {n.label}
                </Button>
              ))}
            </Box>
          )}

          {/* CTA Buttons */}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            {!isMobile && (
              <Button
                onClick={() => navigate(isLoggedIn ? "/dashboard" : "/login")}
                sx={{
                background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                color: "white",
                fontWeight: 700,
                fontSize: { xs: 12, md: 14 },
                px: { xs: 2, md: 2.5 },
                py: 1,
                borderRadius: "10px",
                boxShadow: "0 4px 14px rgba(22,163,74,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
                  boxShadow: "0 6px 20px rgba(22,163,74,0.4)",
                },
              }}
              >
                {isLoggedIn ? "Dashboard" : "Login"}
              </Button>
            )}
            
            {isMobile && (
              <IconButton
                onClick={() => setDrawer(true)}
                sx={{ color: "#374151" }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawer}
        onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: 280, bgcolor: "white", p: 3 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>
            Menu
          </Typography>
          <IconButton onClick={() => setDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ p: 0 }}>
          {NAV.map((n) => (
            <ListItem
              key={n.label}
              onClick={() => {
                navigate(n.href.startsWith("#") ? "/" : n.href);
                setDrawer(false);
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "#f0fdf4" },
              }}
            >
              <Typography sx={{ fontWeight: 600, color: "#374151" }}>
                {n.label}
              </Typography>
            </ListItem>
          ))}
          <Box
            sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate("/login");
                setDrawer(false);
              }}
              sx={{ borderColor: "#e2e8f0", color: "#374151", fontWeight: 600 }}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                navigate("/registration");
                setDrawer(false);
              }}
              sx={{
                background: "linear-gradient(135deg, #16a34a, #22c55e)",
                color: "white",
                fontWeight: 700,
              }}
            >
              Register Free
            </Button>
          </Box>
        </List>
      </Drawer>
    </>
  );
}
